import React, { memo, useState, useCallback, useMemo } from 'react'
import { useHistory, useParams, useRouteMatch } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import { useTranslation } from 'react-i18next'
import Grid from '@material-ui/core/Grid'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { Helmet } from 'react-helmet'

import styles from './styles'
import { TCompanyProps, EEntityAboutSectionType, EEntity } from 'models'
import {
	Informations,
	CompanyInsideTeam,
	CompanyFeed,
	CompanyTeams,
	InviteTeamMembers,
	EntityCompanyLink,
} from './components'
import { EntityAboutSection, EntityHeader, Read, Loading } from 'components'
import { StoreValue } from 'store'
import { AddTeamModal } from './components/teams'
import {
	truncateText,
	buildCompanyCover,
	EnumUserRoles,
	useCheckUserPermission,
} from 'utils'
import {
	useArticlesByEntityQuery,
	useCompanyQuery,
	useTeamQueryQuery,
} from 'graphql/__generated__/graphql'

const useStyles = makeStyles(styles)

const CompanyView: React.FunctionComponent<TCompanyProps> = props => {
	const { storeCompanyPageViewAs } = props
	const history = useHistory()
	const {
		companySlug,
		companySection,
		articleSlug,
		postId,
	}: {
		companySlug?: string
		companySection: string
		articleSlug: string
		postId: string
	} = useParams()
	const classes = useStyles()
	const { t, i18n } = useTranslation()
	const isMobile = useMediaQuery('@media (max-width:650px)')
	const [goToCreateStory, setGoToCreateStory] = useState(false)
	const [openAddTeamModal, setOpenAddTeamModal] = useState(false)
	const currentSection =
		companySection && companySection !== 'story' ? companySection : 'index'
	let match = useRouteMatch({
		path: '/:language(en|ro)/employers/:companySlug',
		strict: true,
		sensitive: true,
	})

	const tabsLabel = {
		theCompany: isMobile ? 'Company' : 'The Company',
		companyPosts: isMobile ? 'Posts' : 'Company posts',
	}

	const tabs: any = {
		index: t(`${tabsLabel.theCompany}`),
		teams: t('Teams'),
		posts: t(`${tabsLabel.companyPosts}`),
	}

	const { data: companyData, loading: loadingCompany } = useCompanyQuery({
		variables: {
			slug: '%' + companySlug + '%',
			// language: i18n.language,
		},
	})

	// const { data: s3Data } = useQuery(S3GetEditorHashQuery, {
	// 	variables: {
	// 		keyStart: companiesFolder + '/',
	// 	},
	// })

	// let s3UploadHash = null
	// if (s3Data && s3Data.getS3UploadHash) {
	// 	s3UploadHash = {
	// 		bucket: s3Data.getS3UploadHash.bucket,
	// 		keyStart: s3Data.getS3UploadHash.keyStart,
	// 		region: s3Data.getS3UploadHash.region,
	// 		params: {
	// 			acl: s3Data.getS3UploadHash.params.acl,
	// 			policy: s3Data.getS3UploadHash.params.policy,
	// 			'x-amz-algorithm': s3Data.getS3UploadHash.params.XAmzAlgorithm,
	// 			'x-amz-credential':
	// 				s3Data.getS3UploadHash.params.XAmzCredential,
	// 			'x-amz-date': s3Data.getS3UploadHash.params.XAmzDate,
	// 			'x-amz-signature': s3Data.getS3UploadHash.params.XAmzSignature,
	// 		},
	// 	}
	// }

	const company = useMemo(
		() =>
			(companyData &&
				companyData?.companyCollection?.edges?.map(x => x.node))?.[0] ||
			null,
		[companyData],
	)

	// get the headquarters
	const companyHq =
		company &&
		company.companyOfficesCollection?.edges.find(item => {
			return item.node.hq
		})
	const { data: teamsData, loading: loadingTeams } = useTeamQueryQuery({
		fetchPolicy: 'network-only',
		variables: {
			companySlug: companySlug,
		},
	})

	const hasUserAccess: boolean = useCheckUserPermission(
		EnumUserRoles.PUBLISHER,
		companyData,
	)

	// const { data, refetch, loading: loadingArticles }: any = useQuery(
	// 	ArticlesByEntity,
	// 	{
	// 		skip: !company,
	// 		fetchPolicy: 'network-only',
	// 		variables: {
	// 			language: i18n.language,
	// 			companyId: company && company.id,
	// 		},
	// 	},
	// )

	const {
		data,
		refetch,
		loading: loadingArticles,
	} = useArticlesByEntityQuery({
		skip: !company,
		fetchPolicy: 'network-only',
		variables: {
			userId: null,
			companyId: company?.id,
			teamId: null,
		},
	})

	const bCanEdit = storeCompanyPageViewAs === 'admin' && hasUserAccess
	const isReady = true
	const articles = useMemo(
		() => (data && data?.feedArticles?.edges) || [],
		[data],
	)

	const toggleTabIndex = useCallback(
		(index: 'index' | 'teams' | 'posts') => {
			let pathname = `/${i18n.language}/employers/${company?.slug}`
			if (index !== 'index') {
				pathname = `/${i18n.language}/employers/${company?.slug}/${index}`
			}

			history.push(pathname, { company: company, keepScroll: true })
		},
		[history, company, i18n.language],
	)

	const handleGoToCreateStory = useCallback(() => {
		setGoToCreateStory(true)
	}, [])

	const toggleAddTeamModal = useCallback(() => {
		setOpenAddTeamModal(state => !state)
	}, [])

	if (loadingCompany || loadingTeams || loadingArticles || !company)
		return <Loading />
	else
		return (
			<>
				{/* here I used 'match' to prevent those meta tags to be on the same page with meta tags from children component */}
				{match && match.isExact && (
					<Helmet>
						<meta
							property="og:title"
							content={company.name ?? undefined}
						/>
						<meta
							property="og:url"
							content={window.location.href}
						/>
						<meta
							property="og:description"
							content={truncateText(
								company.description?.replace(
									/(<([^>]+)>)/gi,
									'',
								) || '',
								100,
							)}
						/>
						{company.hasCover && (
							<meta
								property="og:image"
								content={company && buildCompanyCover(company)}
							/>
						)}
						{company.featuredVideo && (
							<meta
								property="og:video"
								content={company.featuredVideo}
							/>
						)}
					</Helmet>
				)}
				{(articleSlug || postId) && <Read />}
				<Grid
					className={classes.companyContainer}
					container={true}
					direction="row"
					id="company-container"
				>
					<EntityHeader
						toggle={toggleTabIndex}
						currentTabIndex={currentSection}
						name={company.name ?? null}
						description={company.industry && company.industry.key}
						tabs={tabs}
						loading={loadingCompany}
						company={company}
						canEdit={hasUserAccess}
						type={EEntity.company}
						hasCompanyArticles={articles.length > 0 ? true : false} // TODO: Review this code. If I already sent the company, it make no sense to send hasCompanyArticles and so on.
						hasCompanyTeams={
							teamsData &&
							teamsData.feedteams?.edges &&
							teamsData.feedteams?.edges.length > 0
								? true
								: false
						}
						hasCompanyJobs={
							company.jobCollection?.edges &&
							company.jobCollection.edges.length > 0
						}
						handleGoToCreateStory={handleGoToCreateStory}
						toggleAddTeamModal={toggleAddTeamModal}
					/>

					{currentSection === 'index' && (
						<Grid
							id="main-content"
							className={classes.companyMainContent}
							container={true}
							direction="column"
						>
							<Informations
								name={company.name ?? ''}
								dateFounded={company.dateFounded ?? ''}
								industry={company.industry ?? undefined}
								phones={company.phones ?? ''}
								emails={company.emails ?? ''}
								website={company.website ?? ''}
								canEdit={bCanEdit}
								companyId={company.id}
								companySlug={company.slug}
								companyHq={companyHq?.node}
								companyOffices={
									company.companyOfficesCollection
								}
							/>

							<EntityCompanyLink
								toggle={toggleTabIndex}
								companyName={company.name}
								companySlug={company.slug}
							/>
							<EntityAboutSection
								type={EEntityAboutSectionType.companyAbout}
								title={t('About')}
								emphasizedTitle={company.name ?? undefined}
								oneRowTitle={false}
								// s3UploadHash={s3UploadHash}
								content={company.description}
								canEdit={bCanEdit}
								companyId={company.id}
							/>
							{/* {s3UploadHash ? (
								<EntityAboutSection
									type={EEntityAboutSectionType.companyAbout}
									title={t('About')}
									emphasizedTitle={company.name ?? undefined}
									oneRowTitle={false}
									s3UploadHash={s3UploadHash}
									content={company.description}
									canEdit={bCanEdit}
									companyId={company.id}
								/>
							) : null} */}
							<EntityAboutSection
								type={
									EEntityAboutSectionType.companyProductsAndServices
								}
								title={t('Products and services')}
								emphasizedTitle={t('company')}
								// s3UploadHash={s3UploadHash}
								oneRowTitle={false}
								content={company.productsAndServices}
								canEdit={bCanEdit}
								companyId={company.id}
							/>
							{/* {s3UploadHash ? (
								<EntityAboutSection
									type={
										EEntityAboutSectionType.companyProductsAndServices
									}
									title={t('Products and services')}
									emphasizedTitle={t('company')}
									s3UploadHash={s3UploadHash}
									oneRowTitle={false}
									content={company.productsAndServices}
									canEdit={bCanEdit}
									companyId={company.id}
								/>
							) : null} */}
							<CompanyInsideTeam
								companyId={company.id || null}
								articles={articles}
								refetch={refetch}
								canEdit={bCanEdit}
								goToCreateStoryProp={goToCreateStory}
							/>
							{bCanEdit && (
								<InviteTeamMembers
									canEdit={bCanEdit}
									isReady={isReady}
								/>
							)}
						</Grid>
					)}
					{currentSection === 'teams' && (
						<CompanyTeams
							canEdit={bCanEdit}
							companyId={company.id}
							companyName={company.name}
							companyTeamsDescription={company.aboutTeams}
						/>
					)}
					{currentSection === 'posts' && (
						<CompanyFeed
							companySlug={companySlug}
							companyName={company.name}
						/>
					)}
					{currentSection === 'company-settings' &&
						bCanEdit &&
						history.push({
							pathname: `/${i18n.language}/employers/${companySlug}/company-settings`,
						})}
				</Grid>
				<AddTeamModal
					companyId={company.id}
					openAddTeamModal={openAddTeamModal}
					toggleAddTeamModal={toggleAddTeamModal}
				/>
			</>
		)
}

const Company = (props: TCompanyProps) => (
	<StoreValue
		keys={[
			'user.id|storeUserId',
			'user._ig|storeUserIg',
			'user.uid|storeUserUid',
			'company.name|name',
			'user.companyRoles|userCompanyRoles',
			'guides.companyPageViewAs|storeCompanyPageViewAs',
		]}
	>
		<CompanyView {...props} />
	</StoreValue>
)

export default memo(Company)
